import { useState, useMemo, useEffect } from "react"
import { PascosTable } from "../dashboard/components/PascosTable"
import { Search, Filter, Plus, RefreshCw } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { Input, Select, SelectItem, Button, Card, CardBody, Skeleton, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter } from "@heroui/react"
import { useListPascosPaginated, useDeletePasco } from "@src/presentation/hooks/pasco"
import { useStats } from "@src/presentation/hooks/stats"
import { ToastService } from "@src/shared/services/toast.service"
import type { PascoListItemDTO } from "@src/application/pascos/dto"

export default function AllPascosPage() {
    const navigate = useNavigate()
    const [searchQuery, setSearchQuery] = useState("")
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("")
    const [statusFilter, setStatusFilter] = useState<string>("")
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
    const [selectedPasco, setSelectedPasco] = useState<PascoListItemDTO | null>(null)

    // Debounce search query
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery)
        }, 300)

        return () => clearTimeout(timer)
    }, [searchQuery])

    // Fetch pascos with server-side filtering
    const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isFetching, error, refetch } = useListPascosPaginated({
        q: debouncedSearchQuery || undefined,
        status: statusFilter || undefined,
        limit: 20
    })
    const { data: statsData } = useStats()
    const { mutate: deletePasco, isPending: isDeleting } = useDeletePasco({
        onSuccess: () => {
            ToastService.success("PASCO deleted successfully")
            setDeleteConfirmOpen(false)
            setSelectedPasco(null)
            refetch()
        },
        onError: (error) => {
            ToastService.error(`Failed to delete PASCO: ${error.message}`)
        }
    })

    // Flatten the paginated data
    const pascos = data?.pages.flatMap((page: any) => page?.pascos || []) || []
    const isInitialLoading = isLoading && !data
    const isRefetching = isFetching && !isLoading

    const stats = useMemo(() => {
        if (!statsData) {
            return {
                total: pascos.length,
                approved: pascos.filter((p) => p.isApproved).length,
                pending: pascos.filter((p) => !p.isApproved).length,
                completed: pascos.filter((p) => p.processingStatus === "completed").length,
                processing: pascos.filter((p) => p.processingStatus === "processing").length,
                failed: pascos.filter((p) => p.processingStatus === "failed").length,
            };
        }
        const pascoStats = statsData.pasco;
        return {
            total: pascoStats.total_pascos,
            approved: pascoStats.approved,
            pending: pascoStats.pending_approval,
            completed: pascoStats.status_breakdown.completed || 0,
            processing: pascoStats.status_breakdown.processing || 0,
            failed: 0, // Not available in current stats API
        };
    }, [statsData, pascos])

    // const handleDeleteClick = (pasco: PascoListItemDTO) => {
    //     setSelectedPasco(pasco)
    //     setDeleteConfirmOpen(true)
    // }

    const confirmDelete = () => {
        if (selectedPasco) {
            deletePasco(selectedPasco.pascoId)
        }
    }

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <header className="border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-20">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row h-auto sm:h-16 py-3 sm:py-0 items-start sm:items-center justify-between gap-3 sm:gap-0">
                        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
                                <svg className="h-5 w-5 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                                    />
                                </svg>
                            </div>
                            <h1 className="text-lg sm:text-xl font-semibold text-foreground">All PASCOs</h1>
                            <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
                                {pascos.length} {pascos.length === 1 ? "result" : "results"} {isRefetching && '(updating...)'}
                            </span>
                        </div>
                        <div className="flex items-center gap-2 w-full sm:w-auto">
                            <Button
                                color="default"
                                variant="flat"
                                onPress={() => refetch()}
                                isLoading={isLoading}
                                startContent={!isLoading ? <RefreshCw className="h-4 w-4" /> : undefined}
                                className="rounded flex-1 sm:flex-initial"
                                size="sm"
                            >
                                <span className="hidden sm:inline">Refresh</span>
                                <span className="sm:hidden">Refresh</span>
                            </Button>
                            <Button
                                color="primary"
                                onPress={() => navigate("/pascos/new")}
                                startContent={<Plus className="h-4 w-4" />}
                                className="rounded flex-1 sm:flex-initial"
                                size="sm"
                            >
                                <span className="hidden sm:inline">Create PASCO</span>
                                <span className="sm:hidden">Create</span>
                            </Button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="space-y-6">
                    {/* Stats Overview */}
                    <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-6">
                        <Card className="border border-border rounded shadow-none">
                            <CardBody className="p-3 sm:p-4">
                                <p className="text-xs sm:text-sm text-default-500">Total</p>
                                <p className="text-xl sm:text-2xl font-bold text-foreground">{stats.total}</p>
                            </CardBody>
                        </Card>
                        <Card className="border border-success/20 bg-success/5 rounded shadow-none">
                            <CardBody className="p-3 sm:p-4">
                                <p className="text-xs sm:text-sm text-success">Approved</p>
                                <p className="text-xl sm:text-2xl font-bold text-success">{stats.approved}</p>
                            </CardBody>
                        </Card>
                        <Card className="border border-warning/20 bg-warning/5 rounded shadow-none">
                            <CardBody className="p-3 sm:p-4">
                                <p className="text-xs sm:text-sm text-warning">Pending Approval</p>
                                <p className="text-xl sm:text-2xl font-bold text-warning">{stats.pending}</p>
                            </CardBody>
                        </Card>
                        <Card className="border border-success/20 bg-success/5 rounded shadow-none">
                            <CardBody className="p-3 sm:p-4">
                                <p className="text-xs sm:text-sm text-success">Completed</p>
                                <p className="text-xl sm:text-2xl font-bold text-success">{stats.completed}</p>
                            </CardBody>
                        </Card>
                        <Card className="border border-primary/20 bg-primary/5 rounded shadow-none">
                            <CardBody className="p-3 sm:p-4">
                                <p className="text-xs sm:text-sm text-primary">Processing</p>
                                <p className="text-xl sm:text-2xl font-bold text-primary">{stats.processing}</p>
                            </CardBody>
                        </Card>
                        <Card className="border border-danger/20 bg-danger/5 rounded shadow-none">
                            <CardBody className="p-3 sm:p-4">
                                <p className="text-xs sm:text-sm text-danger">Failed</p>
                                <p className="text-xl sm:text-2xl font-bold text-danger">{stats.failed}</p>
                            </CardBody>
                        </Card>
                    </div>

                    {/* Search and Filters */}
                    <Card className="border border-border rounded shadow-none bg-secondary">
                        <CardBody className="p-4">
                            <div className="flex flex-col gap-4">
                                {/* Search */}
                                <Input
                                    type="text"
                                    placeholder="Search by course code, name, or academic year..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    startContent={<Search className="h-4 w-4 text-default-400" />}
                                    classNames={{
                                        input: "text-sm",
                                        inputWrapper: "h-10 rounded",
                                    }}
                                />

                                {/* Filters Row */}
                                <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3">
                                    <div className="flex items-center gap-2">
                                        <Filter className="h-4 w-4 text-default-400" />
                                        <span className="text-sm text-default-500">Filters:</span>
                                    </div>

                                    <Select
                                        label="Status"
                                        placeholder="Select status"
                                        selectedKeys={statusFilter ? [statusFilter] : ["all"]}
                                        onSelectionChange={(keys) => {
                                            const selected = Array.from(keys)[0] as string
                                            setStatusFilter(selected === "all" ? "" : selected)
                                        }}
                                        size="sm"
                                        className="w-full sm:w-40"
                                        classNames={{
                                            trigger: "h-10 rounded",
                                            popoverContent: 'rounded',
                                        }}
                                    >
                                        <SelectItem key="all" className="rounded">All</SelectItem>
                                        <SelectItem key="completed" className="rounded">Completed</SelectItem>
                                        <SelectItem key="processing" className="rounded">Processing</SelectItem>
                                        <SelectItem key="pending" className="rounded">Pending</SelectItem>
                                        <SelectItem key="failed" className="rounded">Failed</SelectItem>
                                    </Select>
                                </div>
                            </div>
                        </CardBody>
                    </Card>

                    {/* Table */}
                    {isInitialLoading ? (
                        <Card className="shadow-none border border-border rounded">
                            <CardBody className="p-0">
                                <div className="space-y-0">
                                    {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                                        <div key={i} className="p-4 border-b border-border last:border-0">
                                            <div className="flex items-center gap-4">
                                                <Skeleton className="h-12 w-12 rounded" />
                                                <div className="flex-1 space-y-2">
                                                    <Skeleton className="h-4 w-3/4 rounded" />
                                                    <Skeleton className="h-3 w-1/2 rounded" />
                                                </div>
                                                <Skeleton className="h-6 w-20 rounded" />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardBody>
                        </Card>
                    ) : error ? (
                        <Card className="shadow-none border border-danger rounded">
                            <CardBody className="p-8">
                                <div className="flex flex-col items-center justify-center text-danger">
                                    <svg className="h-12 w-12 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <p className="font-semibold">Error loading PASCOs</p>
                                    <p className="text-sm mt-1">{error.message}</p>
                                </div>
                            </CardBody>
                        </Card>
                    ) : (
                        <PascosTable
                            pascos={pascos}
                            fetchNextPage={fetchNextPage}
                            hasNextPage={hasNextPage}
                            isFetchingNextPage={isFetchingNextPage}
                        />
                    )}
                </div>
            </main>

            {/* Delete Confirmation Modal */}
            <Modal
                isOpen={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
                classNames={{
                    base: "rounded border border-border",
                    backdrop: "bg-black/50"
                }}
            >
                <ModalContent>
                    <ModalHeader className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                            <svg className="h-5 w-5 text-danger" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            <span>Delete PASCO</span>
                        </div>
                    </ModalHeader>
                    <ModalBody>
                        <p className="text-sm text-default-600">
                            Are you sure you want to delete this PASCO? This action cannot be undone.
                        </p>
                        {selectedPasco && (
                            <div className="mt-4 p-3 bg-danger/10 rounded border border-danger/20">
                                <div className="space-y-2 text-sm">
                                    <div>
                                        <span className="text-default-500">Course: </span>
                                        <span className="font-medium">{selectedPasco.courseCode || 'N/A'} - {selectedPasco.courseName || 'Untitled'}</span>
                                    </div>
                                    {selectedPasco.academicYear && (
                                        <div>
                                            <span className="text-default-500">Academic Year: </span>
                                            <span className="font-medium">{selectedPasco.academicYear}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            variant="flat"
                            onPress={() => setDeleteConfirmOpen(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            color="danger"
                            onPress={confirmDelete}
                            isLoading={isDeleting}
                        >
                            Delete
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </div>
    )
}
